import { AlertTriangleIcon, MicIcon } from "lucide-react";
import { Dialog } from "@radix-ui/react-dialog";
import { DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@component/dialog";
import { Button } from "@component/button";

export type AudioGateVariant = "soft" | "hard";

interface AudioGateModalProps {
    variant: AudioGateVariant;
    open: boolean;
    onClose: () => void;
    onContinueAnyway?: () => void;
    onCheckMicrophone: () => void;
    /**
     * Translator from custom-voice-interview-v2 (en, id, ms).
     * Falls back to English when not provided.
     */
    t?: (en: string, id: string, ms: string) => string | React.ReactNode;
}

/**
 * Pre-interview audio gate.
 *
 * - "soft" — weak audio detected. Candidate may continue anyway or check mic.
 * - "hard" — no microphone / no audio detected. Candidate cannot proceed
 *   until they check their microphone. There is no "Continue anyway".
 */
export default function AudioGateModal({
    variant,
    open,
    onClose,
    onContinueAnyway,
    onCheckMicrophone,
    t,
}: AudioGateModalProps) {
    const tr = t ?? ((en: string) => en);

    const isHard = variant === "hard";

    const header = isHard
        ? tr(
              "No microphone detected",
              "Mikrofon tidak terdeteksi",
              "Mikrofon tidak dikesan",
          )
        : tr(
              "Weak audio detected",
              "Audio lemah terdeteksi",
              "Audio lemah dikesan",
          );

    const body = isHard
        ? tr(
              "We couldn't detect a microphone on your device. Your AI interviewer won't be able to hear you. Please connect a microphone and allow access before continuing.",
              "Kami tidak dapat mendeteksi mikrofon di perangkat Anda. Pewawancara AI tidak akan dapat mendengar Anda. Silakan sambungkan mikrofon dan izinkan akses sebelum melanjutkan.",
              "Kami tidak dapat mengesan mikrofon pada peranti anda. Penemuduga AI tidak akan dapat mendengar anda. Sila sambungkan mikrofon dan benarkan akses sebelum meneruskan.",
          )
        : tr(
              "We're detecting weak audio from your microphone. Your AI interviewer may not hear your responses clearly.\n\nMake sure that you're using the correct microphone, or check your device settings on your computer.",
              "Kami mendeteksi audio yang lemah dari mikrofon Anda. Pewawancara AI mungkin tidak dapat mendengar respons Anda dengan jelas.\n\nPastikan Anda menggunakan mikrofon yang benar, atau periksa pengaturan perangkat di komputer Anda.",
              "Kami mengesan audio yang lemah daripada mikrofon anda. Penemuduga AI mungkin tidak dapat mendengar respons anda dengan jelas.\n\nPastikan anda menggunakan mikrofon yang betul, atau semak tetapan peranti pada komputer anda.",
          );

    const footerNote = isHard
        ? tr(
              "A working microphone is required to begin your AI interview.",
              "Mikrofon yang berfungsi diperlukan untuk memulai wawancara AI Anda.",
              "Mikrofon yang berfungsi diperlukan untuk memulakan temuduga AI anda.",
          )
        : tr(
              "Continuing may affect your interview recording quality.",
              "Melanjutkan dapat mempengaruhi kualitas rekaman wawancara Anda.",
              "Meneruskan boleh menjejaskan kualiti rakaman temuduga anda.",
          );

    const statusLabel = isHard
        ? tr("No audio detected", "Tidak ada audio terdeteksi", "Tiada audio dikesan")
        : tr("Weak audio", "Audio lemah", "Audio lemah");

    const statusBadgeClasses = isHard
        ? "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-600 dark:text-red-400"
        : "bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800 text-yellow-600 dark:text-yellow-400";

    const statusDotClasses = isHard
        ? "bg-red-500"
        : "bg-yellow-500";

    return (
        <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
            <DialogContent className="sm:max-w-md p-6" closeButtonClassName="top-4 right-4">
                <DialogHeader className="flex flex-row items-center gap-3 text-left">
                    <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                        isHard
                            ? "bg-red-50 dark:bg-red-900/20"
                            : "bg-yellow-50 dark:bg-yellow-900/20"
                    }`}>
                        <AlertTriangleIcon className={`h-5 w-5 ${
                            isHard
                                ? "text-red-600 dark:text-red-400"
                                : "text-yellow-600 dark:text-yellow-400"
                        }`} />
                    </div>
                    <DialogTitle className="text-lg font-semibold">{header}</DialogTitle>
                </DialogHeader>

                <DialogDescription className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line">
                    {body}
                </DialogDescription>

                {/* Status row */}
                <div className="flex items-center justify-between rounded-lg border border-border bg-muted/40 px-3 py-2">
                    <span className="inline-flex items-center gap-2 text-sm font-medium text-foreground">
                        <MicIcon className="h-4 w-4 text-foreground" />
                        {tr("Microphone status", "Status mikrofon", "Status mikrofon")}
                    </span>
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium ${statusBadgeClasses}`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${statusDotClasses}`} />
                        {statusLabel}
                    </span>
                </div>

                {/* Footer note */}
                <p className="text-xs italic text-gray-500 dark:text-gray-400">{footerNote}</p>

                {/* CTAs */}
                <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                    {!isHard && (
                        <Button
                            variant="outline"
                            onClick={() => {
                                onClose();
                                onContinueAnyway?.();
                            }}
                        >
                            {tr("Continue anyway", "Lanjutkan saja", "Teruskan sahaja")}
                        </Button>
                    )}
                    <Button
                        onClick={() => {
                            onClose();
                            onCheckMicrophone();
                        }}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                        {tr("Check my microphone", "Periksa mikrofon saya", "Semak mikrofon saya")}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
